import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { StatusBadge } from '../components/StatusBadge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, CheckCircle, XCircle, Loader2, Clock, User, Calendar, History, MessageSquare, Edit, Layers, Eye, X, BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { getCountryName, getCategoryLabel } from '../lib/constants';
import { nameFromEmail, formatFactorValue } from '../lib/format';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmissionFactorDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromPending = searchParams.get('from') === 'pending';
  const [factor, setFactor] = useState(null);
  const [history, setHistory] = useState([]);
  const [similarEFs, setSimilarEFs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [approvalDialog, setApprovalDialog] = useState({ open: false, action: null });
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [similarEFDialog, setSimilarEFDialog] = useState({ open: false, ef: null });
  const { canApprove, canEdit } = useAuth();
  
  const backUrl = fromPending ? '/pending-approvals' : '/emission-factors';

  const fetchFactor = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/emission-factors/${id}`);
      setFactor(response.data);
    } catch (error) {
      toast.error('Failed to load emission factor');
      navigate('/emission-factors');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/emission-factors/${id}/history`);
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  }, [id]);

  const fetchSimilarEFs = useCallback(async () => {
    setLoadingSimilar(true);
    try {
      const response = await axios.get(`${API_URL}/emission-factors/${id}/similar`);
      setSimilarEFs(response.data.similar_factors || []);
    } catch (error) {
      console.error('Failed to load similar emission factors:', error);
    } finally {
      setLoadingSimilar(false);
    }
  }, [id]);

  useEffect(() => {
    fetchFactor();
    fetchHistory();
    fetchSimilarEFs();
  }, [fetchFactor, fetchHistory, fetchSimilarEFs]);

  const handleApproval = async () => {
    setSubmitting(true);
    try {
      // request_correction is sent as 'reject' to backend with comment
      const backendAction = approvalDialog.action === 'request_correction' ? 'reject' : approvalDialog.action;
      
      await axios.post(`${API_URL}/emission-factors/${id}/approve`, {
        action: backendAction,
        comment: comment || null
      });
      
      const successMessage = approvalDialog.action === 'approve' 
        ? 'Emission factor approved successfully' 
        : approvalDialog.action === 'request_correction'
        ? 'Correction request sent to the editor'
        : 'Emission factor rejected';
        
      toast.success(successMessage);
      setApprovalDialog({ open: false, action: null });
      setComment('');
      fetchFactor();
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Emission/numeric values exactly as stored
  const formatEmissionValue = formatFactorValue;

  const DetailRow = ({ label, value }) => (
    <div className="py-3 border-b border-gray-100 last:border-0">
      <dt className="text-sm text-muted-foreground mb-1">{label}</dt>
      <dd className="font-medium">{value ?? '—'}</dd>
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="ef-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(backUrl)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-heading text-2xl font-bold">
                  EF #{factor?.emission_factor_id}
                </h1>
                <StatusBadge status={factor?.status} />
                {factor?.double_verified && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium text-emerald-600 border border-emerald-200 bg-emerald-50"
                    title={factor.double_verified_by ? `Cross-checked by ${factor.double_verified_by}` : 'Cross-checked'}
                    data-testid="cross-checked-pill"
                  >
                    <BadgeCheck className="w-3.5 h-3.5" />
                    Cross-checked
                    {factor.double_verified_by && (
                      <span className="font-normal text-emerald-700/80">· {nameFromEmail(factor.double_verified_by)}</span>
                    )}
                  </span>
                )}
                {factor?.site_identity && String(factor.site_identity).trim() && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-300"
                    title={`Site-specific factor (${factor.site_identity})`}
                    data-testid="site-specific-pill"
                  >
                    Site-specific · {factor.site_identity}
                  </span>
                )}
              </div>
              <p className="text-muted-foreground mt-1">
                {getCategoryLabel(factor?.category)} / {factor?.sub_category}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Edit button for pending factors (editor/approver/admin) */}
            {canEdit() && factor?.status === 'pending' && (
              <Link to={`/emission-factors/${id}/edit`}>
                <Button variant="outline" data-testid="edit-btn">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </Link>
            )}
            {/* Show approve/reject buttons when user has approval role and factor is pending */}
            {canApprove() && factor?.status === 'pending' && (
              <>
                <Button
                  className="gap-2 bg-green-600 hover:bg-green-700"
                  onClick={() => setApprovalDialog({ open: true, action: 'approve' })}
                  data-testid="approve-btn"
                >
                  <CheckCircle className="w-4 h-4" />
                  Approve
                </Button>
                <Button
                  variant="outline"
                  className="gap-2 border-amber-500 text-amber-600 hover:bg-amber-50"
                  onClick={() => setApprovalDialog({ open: true, action: 'request_correction' })}
                  data-testid="request-correction-btn"
                >
                  <MessageSquare className="w-4 h-4" />
                  Request Correction
                </Button>
                <Button
                  variant="destructive"
                  className="gap-2"
                  onClick={() => setApprovalDialog({ open: true, action: 'reject' })}
                  data-testid="reject-btn"
                >
                  <XCircle className="w-4 h-4" />
                  Reject
                </Button>
              </>
            )}
            {factor?.status === 'rejected' && (
              <Button
                className="gap-2 bg-blue-600 hover:bg-blue-700"
                onClick={() => navigate(`/emission-factors/${id}/resubmit`)}
                data-testid="resubmit-btn"
              >
                <Edit className="w-4 h-4" />
                Resubmit with Corrections
              </Button>
            )}
          </div>
        </div>

        {/* Site-specific banner */}
        {factor?.site_identity && String(factor.site_identity).trim() && (
          <div
            className="flex items-start gap-3 rounded-lg border border-blue-300 bg-blue-50 px-4 py-3 text-blue-900"
            data-testid="site-specific-banner"
          >
            <div className="w-8 h-8 rounded-md bg-blue-100 flex items-center justify-center shrink-0">
              <span className="text-xs font-bold text-blue-700">SITE</span>
            </div>
            <div className="flex-1 text-sm">
              <p className="font-semibold">Site-specific emission factor</p>
              <p className="text-blue-800/80 mt-0.5">
                This factor is tied to site identity:{' '}
                <span className="font-mono font-semibold">{factor.site_identity}</span>
              </p>
            </div>
          </div>
        )}

        {/* Meta Info Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-gray-200">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Created</p>
                <p className="font-medium text-sm">{formatDate(factor?.created_at)}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-gray-200">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Last Updated</p>
                <p className="font-medium text-sm">{formatDate(factor?.updated_at)}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-gray-200">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <User className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Approved By</p>
                <p className="font-medium text-sm">
                  {factor?.approved_by_name || factor?.approved_by || '—'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Details, History, Comments, and Similar EFs */}
        <Tabs defaultValue="details" className="space-y-6">
          <TabsList>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <History className="w-4 h-4" />
              Version History ({history.length})
            </TabsTrigger>
            <TabsTrigger value="comments" className="gap-2">
              <MessageSquare className="w-4 h-4" />
              Comments ({history.filter(h => h.comment).length})
            </TabsTrigger>
            <TabsTrigger value="similar" className="gap-2">
              <Layers className="w-4 h-4" />
              Similar EFs ({similarEFs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-6">
            {/* Details Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* General Information */}
              <Card className="border-gray-200">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">General Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-0">
                  <DetailRow label="Category" value={getCategoryLabel(factor?.category)} />
                  <DetailRow label="Activity Classification" value={factor?.activity_classification} />
                  <DetailRow label="Sub Category" value={factor?.sub_category} />
                  <DetailRow label="Type" value={factor?.type} />
                  <DetailRow label="Sub Type" value={factor?.sub_type} />
                  <DetailRow label="Scope" value={factor?.scope} />
                  <DetailRow label="Description" value={factor?.description} />
                  <DetailRow label="Tag" value={factor?.tag} />
                </CardContent>
              </Card>

              {/* Location Information */}
              <Card className="border-gray-200">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Location</CardTitle>
                </CardHeader>
                <CardContent className="space-y-0">
                  <DetailRow label="Continent" value={factor?.continent} />
                  <DetailRow label="Country" value={getCountryName(factor?.country)} />
                  <DetailRow label="Country Code" value={factor?.country} />
                  <DetailRow label="State" value={factor?.state} />
                  <DetailRow label="City" value={factor?.city} />
                  <DetailRow label="Site Identity" value={factor?.site_identity} />
                </CardContent>
              </Card>

              {/* Emission Data - CO2e first */}
              <Card className="border-gray-200">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Emission Data</CardTitle>
                </CardHeader>
                <CardContent className="space-y-0">
                  <DetailRow label="CO2e" value={formatEmissionValue(factor?.co2e)} />
                  <DetailRow label="CO2 Factor" value={formatEmissionValue(factor?.co2factor)} />
                  <DetailRow label="CH4 Factor" value={formatEmissionValue(factor?.ch4factor)} />
                  <DetailRow label="N2O Factor" value={formatEmissionValue(factor?.n2o_factor)} />
                  <DetailRow label="NO2 Factor" value={formatEmissionValue(factor?.no2factor)} />
                  <DetailRow label="CO4 Factor" value={formatEmissionValue(factor?.co4factor)} />
                  <DetailRow label="Biogenic CO2 Factor" value={formatEmissionValue(factor?.biogenic_co2factor)} />
                  <DetailRow label="Input Unit" value={factor?.input_unit} />
                  <DetailRow label="Unit Category" value={factor?.unit_category} />
                </CardContent>
              </Card>

              {/* Source Information */}
              <Card className="border-gray-200">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Source Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-0">
                  <DetailRow label="Source Name" value={factor?.source_name} />
                  <DetailRow label="Source Version" value={factor?.source_version} />
                  <DetailRow label="Source URL" value={factor?.sourceurl} />
                  <DetailRow label="AR Version" value={factor?.ar_version} />
                  <DetailRow label="Is Primary Factor" value={factor?.is_primary_factor ? 'Yes' : 'No'} />
                  <DetailRow label="Is Spend Based" value={factor?.is_spend_based ? 'Yes' : 'No'} />
                </CardContent>
              </Card>

              {/* Water Data */}
              <Card className="border-gray-200">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Water Data</CardTitle>
                </CardHeader>
                <CardContent className="space-y-0">
                  <DetailRow label="Water Use (m³)" value={formatEmissionValue(factor?.water_use_m3)} />
                  <DetailRow label="Water Data Source" value={factor?.water_data_source_name} />
                  <DetailRow label="Water Source Version" value={factor?.water_data_source_version} />
                  <DetailRow label="Water Source URL" value={factor?.water_data_sourceurl} />
                </CardContent>
              </Card>

              {/* Flag Data */}
              <Card className="border-gray-200">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Flag Data</CardTitle>
                </CardHeader>
                <CardContent className="space-y-0">
                  <DetailRow label="Flag CO2e" value={formatEmissionValue(factor?.flag_co2e)} />
                  <DetailRow label="Flag Source Name" value={factor?.flag_source_name} />
                  <DetailRow label="Flag Source Version" value={factor?.flag_source_version} />
                  <DetailRow label="Flag Source URL" value={factor?.flag_sourceurl} />
                </CardContent>
              </Card>

              {/* EPR / Plastic Data */}
              <Card className="border-gray-200">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">EPR / Plastic Data</CardTitle>
                </CardHeader>
                <CardContent className="space-y-0">
                  <DetailRow label="Is Plastic" value={factor?.is_plastic || '—'} />
                  <DetailRow label="Plastic Category" value={factor?.plastic_category || '—'} />
                </CardContent>
              </Card>

              {/* Additional Information */}
              <Card className="border-gray-200 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Additional Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    {factor?.additional_information || 'No additional information provided.'}
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="history">
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Version History</CardTitle>
                <CardDescription>Track all changes made to this emission factor</CardDescription>
              </CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No version history available</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {history.map((entry, index) => (
                      <div 
                        key={entry.id || index}
                        className={`p-4 border rounded-lg ${
                          entry.action === 'reject' ? 'border-red-200 bg-red-50' :
                          entry.action === 'approve' ? 'border-green-200 bg-green-50' :
                          'border-gray-200'
                        }`}
                        data-testid={`history-entry-${index}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              entry.action === 'create' ? 'bg-blue-100 text-blue-800' :
                              entry.action === 'update' ? 'bg-amber-100 text-amber-800' :
                              entry.action === 'approve' ? 'bg-green-100 text-green-800' :
                              entry.action === 'reject' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              v{entry.version} - {entry.action.charAt(0).toUpperCase() + entry.action.slice(1)}
                            </span>
                            <StatusBadge status={entry.status} />
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(entry.created_at)}
                          </span>
                        </div>
                        
                        {/* User info */}
                        <div className="flex items-center gap-2 mb-3 text-sm">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{entry.user_name}</span>
                          <span className="text-muted-foreground">({entry.user_email})</span>
                        </div>
                        
                        {/* Comment section - prominently displayed */}
                        {entry.comment && (
                          <div className={`p-3 rounded-lg mb-3 ${
                            entry.action === 'reject' ? 'bg-red-100 border border-red-200' : 'bg-gray-100'
                          }`}>
                            <div className="flex items-start gap-2">
                              <span className="text-sm font-medium">💬 Comment:</span>
                              <p className="text-sm flex-1">{entry.comment}</p>
                            </div>
                          </div>
                        )}
                        
                        {entry.data && typeof entry.data === 'object' && Object.keys(entry.data).length > 0 && (
                          <div className="text-sm bg-gray-50 p-3 rounded overflow-x-auto">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                              {Object.entries(entry.data).slice(0, 8).map(([key, value]) => (
                                <div key={key}>
                                  <span className="text-muted-foreground">{key}: </span>
                                  <span className="font-mono">{String(value ?? '—')}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="comments">
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Comments</CardTitle>
                <CardDescription>
                  All comments and feedback on this emission factor
                </CardDescription>
              </CardHeader>
              <CardContent>
                {history.filter(h => h.comment).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No comments yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {history.filter(h => h.comment).map((entry, index) => (
                      <div 
                        key={entry.id || index}
                        className={`p-4 border rounded-lg ${
                          entry.action === 'reject' ? 'border-red-200 bg-red-50' :
                          entry.action === 'approve' ? 'border-green-200 bg-green-50' :
                          'border-blue-200 bg-blue-50'
                        }`}
                        data-testid={`comment-entry-${index}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              entry.action === 'approve' ? 'bg-green-100 text-green-800' :
                              entry.action === 'reject' ? 'bg-red-100 text-red-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {entry.action === 'reject' ? 'Rejection Feedback' : 
                               entry.action === 'approve' ? 'Approval Note' : 
                               'Comment'}
                            </span>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(entry.created_at)}
                          </span>
                        </div>
                        
                        <p className="text-sm mb-3">{entry.comment}</p>
                        
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <User className="w-4 h-4" />
                          <span className="font-medium">{entry.user_name}</span>
                          <span>({entry.user_email})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="similar">
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Similar Emission Factors</CardTitle>
                <CardDescription>
                  Emission factors with matching Category, Sub Category, Type, or Sub Type
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingSimilar ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : similarEFs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Layers className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No similar emission factors found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[80px]">EF ID</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Sub Category</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Sub Type</TableHead>
                          <TableHead>Country</TableHead>
                          <TableHead>Unit</TableHead>
                          <TableHead className="text-right">CO2e</TableHead>
                          <TableHead className="text-right">CO2 Factor</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="w-[60px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {similarEFs.map((ef) => {
                          const isSiteSpecific = !!(ef.site_identity && String(ef.site_identity).trim());
                          return (
                          <TableRow
                            key={ef.emission_factor_id}
                            className={isSiteSpecific ? 'bg-blue-50/60 hover:bg-blue-50 border-l-4 border-l-blue-500' : ''}
                            data-testid={`similar-ef-${ef.emission_factor_id}`}
                            data-site-specific={isSiteSpecific ? 'true' : 'false'}
                          >
                            <TableCell className="font-medium">
                              <div className="flex flex-col items-start gap-1">
                                <span>#{ef.emission_factor_id}</span>
                                {isSiteSpecific && (
                                  <span
                                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700 border border-blue-300"
                                    title={`Site-specific factor (${ef.site_identity})`}
                                  >
                                    Site
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>{getCategoryLabel(ef.category)}</TableCell>
                            <TableCell>{ef.sub_category || '—'}</TableCell>
                            <TableCell>{ef.type || '—'}</TableCell>
                            <TableCell>{ef.sub_type || '—'}</TableCell>
                            <TableCell>{getCountryName(ef.country) || ef.country || '—'}</TableCell>
                            <TableCell>{ef.input_unit || '—'}</TableCell>
                            <TableCell className="text-right font-mono">
                              {formatFactorValue(ef.co2e)}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatFactorValue(ef.co2factor)}
                            </TableCell>
                            <TableCell>
                              <StatusBadge status={ef.status} />
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setSimilarEFDialog({ open: true, ef })}
                                data-testid={`view-similar-${ef.emission_factor_id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Approval Dialog */}
        <Dialog open={approvalDialog.open} onOpenChange={(open) => setApprovalDialog({ open, action: approvalDialog.action })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {approvalDialog.action === 'approve' ? 'Approve' : 
                 approvalDialog.action === 'request_correction' ? 'Request Correction' : 'Reject'} Emission Factor
              </DialogTitle>
              <DialogDescription>
                {approvalDialog.action === 'approve'
                  ? 'This emission factor will be marked as approved and become active.'
                  : approvalDialog.action === 'request_correction'
                  ? 'Send feedback to the editor about required corrections. The emission factor will be rejected.'
                  : 'This emission factor will be rejected and marked for review.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {(approvalDialog.action === 'reject' || approvalDialog.action === 'request_correction') && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  💡 {approvalDialog.action === 'request_correction' 
                    ? 'Comment is required. Please describe what corrections are needed.' 
                    : 'Adding a comment helps the editor understand what went wrong.'}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="comment">
                  Comment {approvalDialog.action === 'request_correction' ? <span className="text-destructive">*</span> : '(optional)'}
                </Label>
                <Textarea
                  id="comment"
                  placeholder={approvalDialog.action === 'request_correction' 
                    ? "Please describe the corrections needed (required)..."
                    : "Add a comment..."}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className={approvalDialog.action !== 'approve' ? 'min-h-[100px]' : ''}
                  data-testid="approval-comment"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setApprovalDialog({ open: false, action: null }); setComment(''); }} data-testid="cancel-approval">
                Cancel
              </Button>
              <Button
                onClick={handleApproval}
                disabled={submitting || (approvalDialog.action === 'request_correction' && !comment.trim())}
                className={approvalDialog.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 
                          approvalDialog.action === 'request_correction' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-destructive'}
                data-testid="confirm-approval"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {approvalDialog.action === 'approve' ? 'Approve' : 
                 approvalDialog.action === 'request_correction' ? 'Send & Reject' : 'Reject'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Similar EF Detail Dialog */}
        <Dialog open={similarEFDialog.open} onOpenChange={(open) => setSimilarEFDialog({ open, ef: similarEFDialog.ef })}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Similar Emission Factor #{similarEFDialog.ef?.emission_factor_id}</span>
                <StatusBadge status={similarEFDialog.ef?.status} />
              </DialogTitle>
              <DialogDescription>
                Complete details for this emission factor
              </DialogDescription>
            </DialogHeader>
            {similarEFDialog.ef && (
              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h4 className="font-medium mb-3 text-primary">Basic Information</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg">
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">EF ID</p>
                      <p className="font-mono text-sm font-bold">#{similarEFDialog.ef.emission_factor_id}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Category</p>
                      <p className="text-sm">{getCategoryLabel(similarEFDialog.ef.category)}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Sub Category</p>
                      <p className="text-sm">{similarEFDialog.ef.sub_category || '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Type</p>
                      <p className="text-sm">{similarEFDialog.ef.type || '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Sub Type</p>
                      <p className="text-sm">{similarEFDialog.ef.sub_type || '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Scope</p>
                      <p className="text-sm">{similarEFDialog.ef.scope || '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Tag</p>
                      <p className="text-sm">{similarEFDialog.ef.tag || '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Description</p>
                      <p className="text-sm">{similarEFDialog.ef.description || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Location Information */}
                <div>
                  <h4 className="font-medium mb-3 text-primary">Location</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg">
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Country</p>
                      <p className="text-sm">{getCountryName(similarEFDialog.ef.country) || similarEFDialog.ef.country || '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">State</p>
                      <p className="text-sm">{similarEFDialog.ef.state || '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">City</p>
                      <p className="text-sm">{similarEFDialog.ef.city || '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Site Identity</p>
                      <p className="text-sm">{similarEFDialog.ef.site_identity || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Emission Values */}
                <div>
                  <h4 className="font-medium mb-3 text-primary">Emission Values</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-xs font-medium text-blue-600">CO2e</p>
                      <p className="text-lg font-bold text-blue-800">
                        {formatFactorValue(similarEFDialog.ef.co2e)}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <p className="text-xs font-medium text-gray-600">CO2 Factor</p>
                      <p className="text-lg font-bold text-gray-800">
                        {formatFactorValue(similarEFDialog.ef.co2factor)}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <p className="text-xs font-medium text-gray-600">CH4 Factor</p>
                      <p className="text-lg font-bold text-gray-800">
                        {formatFactorValue(similarEFDialog.ef.ch4factor)}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <p className="text-xs font-medium text-gray-600">N2O Factor</p>
                      <p className="text-lg font-bold text-gray-800">
                        {formatFactorValue(similarEFDialog.ef.n2o_factor)}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <p className="text-xs font-medium text-gray-600">NO2 Factor</p>
                      <p className="text-lg font-bold text-gray-800">
                        {formatFactorValue(similarEFDialog.ef.no2factor)}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <p className="text-xs font-medium text-gray-600">CO4 Factor</p>
                      <p className="text-lg font-bold text-gray-800">
                        {formatFactorValue(similarEFDialog.ef.co4factor)}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <p className="text-xs font-medium text-gray-600">Biogenic CO2 Factor</p>
                      <p className="text-lg font-bold text-gray-800">
                        {formatFactorValue(similarEFDialog.ef.biogenic_co2factor)}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <p className="text-xs font-medium text-gray-600">Flag CO2e</p>
                      <p className="text-lg font-bold text-gray-800">
                        {formatFactorValue(similarEFDialog.ef.flag_co2e)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Unit Information */}
                <div>
                  <h4 className="font-medium mb-3 text-primary">Unit Information</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg">
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Input Unit</p>
                      <p className="text-sm font-medium">{similarEFDialog.ef.input_unit || '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Unit Category</p>
                      <p className="text-sm">{similarEFDialog.ef.unit_category || '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Is Spend Based</p>
                      <p className="text-sm">{similarEFDialog.ef.is_spend_based ? 'Yes' : 'No'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Is Primary Factor</p>
                      <p className="text-sm">{similarEFDialog.ef.is_primary_factor ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                </div>

                {/* Source Information */}
                <div>
                  <h4 className="font-medium mb-3 text-primary">Source Information</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Source Name</p>
                      <p className="text-sm">{similarEFDialog.ef.source_name || '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Source Version</p>
                      <p className="text-sm">{similarEFDialog.ef.source_version || '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">AR Version</p>
                      <p className="text-sm">{similarEFDialog.ef.ar_version || '—'}</p>
                    </div>
                    <div className="space-y-1 col-span-2 md:col-span-3">
                      <p className="text-xs font-medium text-muted-foreground">Source URL</p>
                      <p className="text-sm break-all">{similarEFDialog.ef.sourceurl || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Flag Source Information */}
                <div>
                  <h4 className="font-medium mb-3 text-primary">Flag Source Information</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Flag Source Name</p>
                      <p className="text-sm">{similarEFDialog.ef.flag_source_name || '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Flag Source Version</p>
                      <p className="text-sm">{similarEFDialog.ef.flag_source_version || '—'}</p>
                    </div>
                    <div className="space-y-1 col-span-2 md:col-span-1">
                      <p className="text-xs font-medium text-muted-foreground">Flag Source URL</p>
                      <p className="text-sm break-all">{similarEFDialog.ef.flag_sourceurl || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Water Data Information */}
                <div>
                  <h4 className="font-medium mb-3 text-primary">Water Data Information</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg">
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Water Use (m³)</p>
                      <p className="text-sm">{formatFactorValue(similarEFDialog.ef.water_use_m3)}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Water Data Source Name</p>
                      <p className="text-sm">{similarEFDialog.ef.water_data_source_name || '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Water Data Source Version</p>
                      <p className="text-sm">{similarEFDialog.ef.water_data_source_version || '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Water Data Source URL</p>
                      <p className="text-sm break-all">{similarEFDialog.ef.water_data_sourceurl || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div>
                  <h4 className="font-medium mb-3 text-primary">Additional Information</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm whitespace-pre-wrap">{similarEFDialog.ef.additional_information || 'No additional information available.'}</p>
                  </div>
                </div>

                {/* Meta Information */}
                <div>
                  <h4 className="font-medium mb-3 text-primary">Meta Information</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg">
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Status</p>
                      <StatusBadge status={similarEFDialog.ef.status} />
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Version</p>
                      <p className="text-sm">{similarEFDialog.ef.version || '1'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Created At</p>
                      <p className="text-sm">{similarEFDialog.ef.created_at ? new Date(similarEFDialog.ef.created_at).toLocaleString() : '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Created By</p>
                      <p className="text-sm">{similarEFDialog.ef.created_by || '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Approved By</p>
                      <p className="text-sm">{similarEFDialog.ef.approved_by_name || similarEFDialog.ef.approved_by || '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Is Deleted</p>
                      <p className="text-sm">{similarEFDialog.ef.is_deleted ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setSimilarEFDialog({ open: false, ef: null })}>
                Close
              </Button>
              <Button onClick={() => navigate(`/emission-factors/${similarEFDialog.ef?.emission_factor_id}`)}>
                View Full Details Page
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
